import { type ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ScrollTrigger, destroyLenis, getLenis, initLenis } from "../../animations/gsapSetup.ts";
import { CustomCursor } from "../common/CustomCursor.tsx";
import { WebGLBackground } from "../common/WebGLBackground.tsx";
import { FontPresetSwitcher } from "../dev/FontPresetSwitcher.tsx";
import { WebGLPresetSwitcher } from "../dev/WebGLPresetSwitcher.tsx";
import { Footer } from "./Footer.tsx";
import { Header } from "./Header.tsx";

const isDev = import.meta.env.DEV;

// ── ページコンテキスト別WebGLプリセット自動切替 ──
// ルートのラストセグメントからプリセットを判定。
// WebGLBackground.tsx の既存イベント系統 (webgl-preset-change) を再利用。
const ROUTE_PRESETS: Record<string, number> = {
  aiteam: 5, // Neural Flow — AI相関発見
  ai: 6, // Ink Flow — 有機的な美（Transcriptionは形が出すぎるため保留）
  classic: 6, // Ink Flow — ブランドの有機的な美
};

function getPresetForRoute(pathname: string): number {
  const segments = pathname.split("/").filter(Boolean);
  const last = segments[segments.length - 1] || "";
  return ROUTE_PRESETS[last] ?? 6;
}

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);

  // Initialize Lenis smooth scroll (desktop only — mobile uses native scroll)
  useEffect(() => {
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    initLenis();
    return () => {
      destroyLenis();
    };
  }, []);

  useLayoutEffect(() => {
    if (prevPathRef.current !== pathname) {
      // Scroll to top — don't kill ScrollTriggers here;
      // let useGSAP's component lifecycle handle cleanup via key={pathname} remount.
      const lenis = getLenis();
      if (lenis) {
        lenis.scrollTo(0, { immediate: true });
      } else {
        window.scrollTo(0, 0);
      }

      // ページコンテキスト別プリセット自動切替
      const presetIdx = getPresetForRoute(pathname);
      window.dispatchEvent(new CustomEvent("webgl-preset-change", { detail: presetIdx }));

      prevPathRef.current = pathname;
    }
  }, [pathname]);

  // Refresh ScrollTrigger positions after route change settles
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  return (
    <HelmetProvider>
      <WebGLBackground />
      <Header />
      <main key={pathname} style={{ paddingTop: "var(--header-height)" }}>{children}</main>
      <Footer />
      <CustomCursor />
      {isDev && <FontPresetSwitcher />}
      {isDev && <WebGLPresetSwitcher />}
    </HelmetProvider>
  );
}
